/**
 * 행동 기록(activity_events)에 들어가는 글자의 개인정보 가리기 — 프론트(보내기 전)와 서버(저장 전)가
 * 같은 규칙을 쓴다(2026-10-05 보안 검토). 행동 기록은 S3 로 영구 백업되므로 «화면 가리개»
 * (privacy-mask.ts — 일부만 별표)와 달리 **통째로 바꿔치기**한다: 전화번호·주민번호·이메일·긴 숫자열 → «[번호]»·«[이메일]».
 *
 * 이름은 모양으로 가릴 수 없다 — 그래서 이름은 «애초에 글자를 안 줍는» 쪽(src/lib/activityName.ts)이 막는다.
 */

import { maskSecretPath } from "./secret-path.js";

export const REDACT_NUMBER = "[번호]";
export const REDACT_EMAIL = "[이메일]";

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// 주민번호(6-7) · 전화번호(0으로 시작 2~3 + 3~4 + 4, 구분자 - . 공백) · 그 밖에 숫자 6개 이상(구분자 섞여도)
const RRN_RE = /\d{6}[-\s]?[1-8]\d{6}/g;
const PHONE_RE = /0\d{1,2}[-.\s]?\d{3,4}[-.\s]?\d{4}/g;
const LONG_DIGITS_RE = /\d(?:[-.\s]?\d){5,}/g;

/** 글자 안의 전화번호·주민번호·이메일·긴 숫자열을 통째로 바꾼다. 문자열이 아니면 그대로. */
export function redactActivityText<T>(value: T): T {
  if (typeof value !== "string" || value === "") return value;
  return value
    .replace(EMAIL_RE, REDACT_EMAIL)
    .replace(RRN_RE, REDACT_NUMBER)
    .replace(PHONE_RE, REDACT_NUMBER)
    .replace(LONG_DIGITS_RE, REDACT_NUMBER) as T;
}

/** user agent — 브라우저 버전(«Chrome/141.0.0.0»)이 긴 숫자열 규칙에 걸려 지워지지 않게 그 규칙만 뺀다.
 *  전화·주민·이메일 꼴 가리기는 그대로 둔다(UA 에 사람 정보가 실릴 일은 없지만 그물은 유지). */
export function redactUserAgent<T>(value: T): T {
  if (typeof value !== "string" || value === "") return value;
  return value.replace(EMAIL_RE, REDACT_EMAIL).replace(RRN_RE, REDACT_NUMBER).replace(PHONE_RE, REDACT_NUMBER) as T;
}

/** 경로 — 쿼리스트링·해시(검색어가 실린다)를 떼고, 주소 안의 비밀 조각(공개 창구 토큰)을 `:token` 으로 바꾼 뒤 가린다.
 *  행동 기록은 S3 로 영구 백업되므로 `/form/<토큰>` 원문이 남으면 그 문이 영구히 열린다(2026-10-06, shared/secret-path.ts). */
export function redactActivityPath(path: string | null | undefined): string | null {
  if (typeof path !== "string" || path === "") return null;
  return redactActivityText(maskSecretPath(path.split(/[?#]/)[0]));
}

/** referrer — 출처 도메인(origin)만. 경로·쿼리엔 검색어·토큰이 실린다. */
export function referrerOrigin(ref: string | null | undefined): string | null {
  if (!ref) return null;
  try {
    const u = new URL(ref);
    return u.protocol === "http:" || u.protocol === "https:" ? u.origin : null;
  } catch {
    return null;
  }
}

/** meta 안의 문자열 값을 전부(깊이까지) 가린다. 키 이름은 코드가 정한 것이라 그대로. */
export function redactActivityMeta(meta: unknown, depth = 0): unknown {
  if (depth > 5) return null;
  if (typeof meta === "string") return redactActivityText(meta);
  if (Array.isArray(meta)) return meta.map((v) => redactActivityMeta(v, depth + 1));
  if (meta && typeof meta === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(meta as Record<string, unknown>)) {
      out[k] =
        k === "referrer" && typeof v === "string"
          ? referrerOrigin(v)
          : k === "userAgent" && typeof v === "string"
            ? redactUserAgent(v)
            : redactActivityMeta(v, depth + 1);
    }
    return out;
  }
  return meta;
}
